import { useEffect, useMemo, useState } from 'react';
import {
  platformNotificationsApi,
  type PlatformMessageTemplate,
} from '@/core/api/platformNotificationsApi';
import { getErrorMessage } from '@/core/utils/errors';
import { AppIcon } from '@/shared/components/AppIcon';

interface TemplateDraft {
  isEnabled: boolean;
  message: string;
}

const faDate = (value?: string | null): string => {
  if (!value) return 'قالب پیش‌فرض سیستم';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat('fa-IR-u-ca-persian', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const channelLabel = (channel: string): string => channel === 'sms' ? 'پیامک' : channel;

export const PlatformMessageTemplatesPage = (): React.ReactElement => {
  const [templates, setTemplates] = useState<PlatformMessageTemplate[]>([]);
  const [selectedEventCode, setSelectedEventCode] = useState('');
  const [draft, setDraft] = useState<TemplateDraft>({ isEnabled: true, message: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.eventCode === selectedEventCode) ?? null,
    [selectedEventCode, templates],
  );

  const load = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await platformNotificationsApi.templates();
      setTemplates(result);
      setSelectedEventCode((current) => current && result.some((item) => item.eventCode === current) ? current : (result[0]?.eventCode ?? ''));
    } catch (exception) {
      setError(getErrorMessage(exception));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  useEffect(() => {
    if (!selectedTemplate) return;
    setDraft({ isEnabled: selectedTemplate.isEnabled, message: selectedTemplate.message });
  }, [selectedTemplate]);

  const selectTemplate = (template: PlatformMessageTemplate): void => {
    setNotice(null);
    setError(null);
    setSelectedEventCode(template.eventCode);
  };

  const insertVariable = (variable: string): void => {
    setDraft((current) => ({
      ...current,
      message: `${current.message}${current.message && !current.message.endsWith(' ') ? ' ' : ''}{{${variable}}}`,
    }));
  };

  const resetDraft = (): void => {
    if (selectedTemplate) setDraft({ isEnabled: selectedTemplate.isEnabled, message: selectedTemplate.message });
    setNotice(null);
  };

  const save = async (): Promise<void> => {
    if (!selectedTemplate || !draft.message.trim()) return;
    setIsSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await platformNotificationsApi.updateTemplate(selectedTemplate.eventCode, {
        isEnabled: draft.isEnabled,
        message: draft.message.trim(),
      });
      setTemplates((current) => current.map((item) => item.eventCode === saved.eventCode ? saved : item));
      setDraft({ isEnabled: saved.isEnabled, message: saved.message });
      setNotice(`قالب «${saved.name}» ذخیره شد و از این پس در همان فرایند استفاده می‌شود.`);
    } catch (exception) {
      setError(getErrorMessage(exception));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-74px)] text-slate-900" dir="rtl">
      <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-7">
        <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-bold text-brand-600">System message templates</p>
            <h1 className="mt-1 text-2xl font-black text-ink-950 sm:text-3xl">قالب‌های پیام فرایندها</h1>
            <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-500">هر پیام سیستمی قالب اختصاصی دارد. متن اینجا فقط روی همان فرایند اثر می‌گذارد؛ مثلاً ایجاد حساب شرکت یا ارسال مجدد اطلاعات ورود.</p>
          </div>
          <div className="w-fit rounded-2xl border border-violet-100 bg-violet-50 px-4 py-3 text-xs font-bold leading-6 text-violet-700">قالب غیرفعال باعث می‌شود پیام آن فرایند ارسال نشود.</div>
        </section>

        {error ? <div className="flex items-start justify-between gap-3 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold leading-7 text-rose-700"><span>{error}</span><button aria-label="بستن خطا" className="shrink-0" onClick={() => setError(null)} type="button"><AppIcon name="close" size={17} /></button></div> : null}
        {notice ? <div className="flex items-start gap-2 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-bold leading-7 text-emerald-700"><AppIcon className="mt-1 shrink-0" name="check" size={17} /><span>{notice}</span></div> : null}

        {isLoading ? <div className="h-80 animate-pulse rounded-3xl bg-white shadow-sm" /> : null}

        {!isLoading ? (
          <section className="grid min-w-0 gap-6 xl:grid-cols-[minmax(280px,0.7fr)_minmax(0,1.3fr)]">
            <aside className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="flex items-center gap-3 px-2 pb-4"><span className="grid size-10 place-items-center rounded-2xl bg-brand-50 text-brand-600"><AppIcon name="document" size={20} /></span><div><h2 className="font-black text-ink-950">فرایندهای دارای پیام</h2><p className="mt-1 text-xs text-slate-400">یک فرایند را برای ویرایش قالب انتخاب کنید.</p></div></div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                {templates.map((template) => {
                  const selected = template.eventCode === selectedEventCode;
                  return <button className={`min-w-0 rounded-2xl border p-4 text-right transition ${selected ? 'border-brand-400 bg-brand-50 shadow-sm' : 'border-slate-100 bg-slate-50/60 hover:border-brand-200 hover:bg-white'}`} key={template.eventCode} onClick={() => selectTemplate(template)} type="button">
                    <div className="flex items-start justify-between gap-3"><span className="min-w-0 text-sm font-black text-slate-800">{template.name}</span><span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-black ${template.isEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{template.isEnabled ? 'فعال' : 'غیرفعال'}</span></div>
                    <p className="mt-2 text-[11px] font-bold text-brand-600">کانال: {channelLabel(template.channel)}</p>
                    <p className="mt-2 line-clamp-2 break-words text-xs leading-6 text-slate-400">{template.message}</p>
                  </button>;
                })}
              </div>
              {!templates.length ? <div className="rounded-2xl border border-dashed border-slate-200 p-7 text-center text-sm text-slate-400">فرایندی برای قالب پیام تعریف نشده است.</div> : null}
            </aside>

            {selectedTemplate ? <article className="min-w-0 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><h2 className="text-xl font-black text-ink-950">{selectedTemplate.name}</h2><span className="rounded-full bg-brand-50 px-3 py-1 text-[11px] font-black text-brand-700">{channelLabel(selectedTemplate.channel)}</span></div><p className="mt-2 text-sm leading-7 text-slate-500">این متن بلافاصله پیش از ارسال با اطلاعات واقعی شرکت و مدیر جایگزین می‌شود.</p></div>
                <button aria-checked={draft.isEnabled} aria-label="فعال‌بودن قالب" className={`relative h-8 w-14 shrink-0 rounded-full transition ${draft.isEnabled ? 'bg-emerald-500' : 'bg-slate-200'}`} onClick={() => setDraft((current) => ({ ...current, isEnabled: !current.isEnabled }))} role="switch" type="button"><span className={`absolute top-1 size-6 rounded-full bg-white shadow transition ${draft.isEnabled ? 'left-1' : 'left-7'}`} /></button>
              </div>

              {!draft.isEnabled ? <p className="mt-5 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-xs font-bold leading-6 text-amber-700">این قالب غیرفعال است؛ تا زمانی که دوباره فعال نشود، پیامک این فرایند ارسال نخواهد شد.</p> : null}

              <label className="mt-6 block"><span className="mb-2 flex items-center justify-between gap-3 text-xs font-bold text-slate-500"><span>متن قالب پیامک</span><span>{new Intl.NumberFormat('fa-IR').format(draft.message.length)} / ۲٬۰۰۰</span></span><textarea className="min-h-64 w-full resize-y rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-8 text-slate-700 outline-none focus:border-brand-500 focus:ring-4 focus:ring-brand-50" maxLength={2000} onChange={(event) => setDraft((current) => ({ ...current, message: event.target.value }))} value={draft.message} /></label>

              <div className="mt-5 rounded-2xl border border-brand-100 bg-brand-50/50 p-4"><p className="text-xs font-black text-brand-800">متغیرهای مجاز این فرایند</p><p className="mt-1 text-[11px] leading-6 text-slate-500">روی هر متغیر بزنید تا به انتهای متن افزوده شود.</p><div className="mt-3 flex flex-wrap gap-2">{selectedTemplate.variables.map((variable) => <button className="rounded-xl border border-brand-100 bg-white px-3 py-2 text-xs font-bold text-brand-700 hover:bg-brand-50" key={variable.key} onClick={() => insertVariable(variable.key)} type="button"><span className="font-sans">{`{{${variable.key}}}`}</span><span className="mr-1.5 text-slate-400">{variable.label}</span></button>)}</div></div>

              <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between"><p className="text-xs leading-6 text-slate-400">آخرین ذخیره: {faDate(selectedTemplate.updatedAtUtc)}</p><div className="flex flex-col gap-2 sm:flex-row"><button className="rounded-xl border border-slate-200 px-4 py-2.5 text-xs font-black text-slate-600" disabled={isSaving} onClick={resetDraft} type="button">لغو تغییرات</button><button className="rounded-xl bg-brand-600 px-5 py-2.5 text-xs font-black text-white shadow-lg shadow-brand-500/20 disabled:opacity-50" disabled={isSaving || !draft.message.trim()} onClick={() => void save()} type="button">{isSaving ? 'در حال ذخیره…' : 'ذخیره قالب'}</button></div></div>
            </article> : null}
          </section>
        ) : null}
      </div>
    </main>
  );
};
